<template>
  <AuthenticatedLayout activeMenuItem='content-library' title='Content Library'>
    <PaddedLayout>
      <div class='flex flex-wrap py-2 justify-between items-center gap-2 max-w-[80rem]'>
        <h1 class='text-2xl'>Content Library</h1>
        <SearchInput v-model='state.keywords' class='w-full sm:w-auto' />
      </div>
      <div class='mt-6 max-w-[80rem]'>
        <TabView :tabs='tabs' v-model='state.tab' />

        <template v-if='state.tab === "sources"'>
          <LoadingLayout :isLoading='sourcesQuery.isLoading()'>
            <h2 class='text-lg mt-4'>Sources Used to Develop the Stewardship Objectives and Performance Ratings:</h2>
            <ul class='mt-2 text-sm divide-y border-t border-b'>
              <li v-for='source in sourcesQuery.getItems()' class='py-1'>
                {{ source.name }}
              </li>
            </ul>
          </LoadingLayout>
        </template>

        <template v-else>
          <LoadingLayout :isLoading='objectivesQuery.isLoading()'>
            <button v-if='state.objective' class='mb-4 flex items-center gap-2 text-sm xl:hidden mt-4' @click='openDrawer'>
              <Bars3BottomLeftIcon class='w-5 h-5' /> Objectives
            </button>

            <div class='overflow-hidden bg-white shadow sm:rounded-lg flex mt-6'>
              <ContentLibraryDrawer :state='state' :objectives='objectives' />

              <aside class='hidden w-96 border-r border-gray-200 xl:block shrink-0'>
                <ContentLibrarySidebar :state='state' :objectives='objectives' class='h-full' />
              </aside>
              <div class="flex w-full">
                <ContentLibraryObjective :objective='state.objective' v-if='state.objective' class='h-full' />
                <div class='flex-1 m-6 border-dashed border-2 rounded-lg border-gray-300 flex items-center justify-center text-gray-400 text-xl text-center p-4 flex-col gap-4' v-else>
                  Choose an objective from the list.
                  <button type="button" class="rounded-md bg-jananavy-900 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-jananavy-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-jananavy-600 xl:hidden" @click='openDrawer'>Show Objectives</button>
                </div>
              </div>
            </div>
          </LoadingLayout>
        </template>

      </div>
    </PaddedLayout>
  </AuthenticatedLayout>
</template>

<script setup>

import { computed, watch, reactive } from 'vue';

import { Bars3BottomLeftIcon } from '@heroicons/vue/24/outline';

import AuthenticatedLayout from '@/components/layouts/authenticated-layout.vue';
import PaddedLayout from '@/components/layouts/padded-layout.vue';
import LoadingLayout from '@/components/layouts/loading-layout.vue';

import TabView from '@/components/views/tab-view.vue';

import SearchInput from '@/components/inputs/search-input.vue';

import ContentLibraryDrawer from '@/components/content-library/content-library-drawer.vue';
import ContentLibrarySidebar from '@/components/content-library/content-library-sidebar.vue';
import ContentLibraryObjective from '@/components/content-library/content-library-objective.vue';

import { usePayloadQuery } from '@/kernel/api';
import { getCollection } from '@/kernel/collections';

const objectiveCollection = getCollection('objective');
const themeCollection = getCollection('theme');
const sourceCollection = getCollection('source');

const state = reactive({
  tab: null,
  objective: null,
  drawerOpen: false,
  keywords: null
});

const objectivesQuery = usePayloadQuery(objectiveCollection.getApiIndexEndpoint(), {
  sort: objectiveCollection.getDefaultSort(),
  limit: 1000
});

const sourcesQuery = usePayloadQuery(sourceCollection.getApiIndexEndpoint(), {
  sort: sourceCollection.getDefaultSort(),
  limit: 1000,
  filters: {
    isPublished: {equals: true}
  }
});

const openDrawer = () => {
  state.drawerOpen = true;
}

const themes = computed(() => {
  const themes = {};

  objectivesQuery.getItems().forEach((item) => {
    if (state.keywords && !item.name.toLowerCase().includes(state.keywords.toLowerCase())) {
      return;
    }

    const themeId = themeCollection.getId(item.theme);

    if (!themes[themeId]) {
      themes[themeId] = {
        ...item.theme,
        objectives: []
      }
    }

    themes[themeId].objectives.push(item);
  });

  Object.values(themes).forEach((theme) => {
    theme.objectives.sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
  });

  return Object.values(themes).toSorted((a, b) => {
    return a.name.localeCompare(b.name);
  });
});

const tabs = computed(() => {
  const result = [];

  if (themes.value) {
    themes.value.forEach((theme) => {
      result.push({
        name: theme.name,
        key: theme.id
      });
    });
  }

  if (sourcesQuery.hasItems()) {
    result.push({
      name: 'Sources',
      key: 'sources'
    });
  }

  return result;
});

const objectives = computed(() => {
  const theme = themes.value.find(theme => theme.id === state.tab);

  if (!theme) {
    return [];
  }

  return theme.objectives;
});

watch(tabs, () => {
  const available = tabs.value.find(tab => tab.key === state.tab);

  if (!available && tabs.value.length) {
    state.tab = tabs.value[0].key;
  }
});

objectivesQuery.doQuery();
sourcesQuery.doQuery();

</script>
